import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  CreditCard, Shield, Lock, CheckCircle2, ArrowRight, 
  Wifi, Network, Tv, Phone, Zap, Clock, BadgeCheck,
  ChevronLeft, Loader2, AlertCircle
} from 'lucide-react';

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  
  const [customerDetails, setCustomerDetails] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    pincode: ''
  });

  // Internet Plans with pricing
  const internetPlans = [
    {
      id: 'basic-50',
      name: 'Basic',
      speed: '50 Mbps',
      icon: <Wifi className="h-8 w-8" />,
      monthlyPrice: 399,
      quarterlyPrice: 1099,
      yearlyPrice: 3999,
      features: ['Unlimited Data', 'Free Router', '24/7 Support', 'Free Installation'],
      color: 'from-blue-500 to-cyan-500',
      popular: false
    },
    {
      id: 'speed-100',
      name: 'Speed',
      speed: '100 Mbps',
      icon: <Zap className="h-8 w-8" />,
      monthlyPrice: 599,
      quarterlyPrice: 1699,
      yearlyPrice: 5999,
      features: ['Unlimited Data', 'Free Router', '24/7 Support', 'Free Installation', 'Static IP'],
      color: 'from-green-500 to-emerald-500',
      popular: true
    },
    {
      id: 'ultra-200',
      name: 'Ultra',
      speed: '200 Mbps',
      icon: <Network className="h-8 w-8" />,
      monthlyPrice: 799,
      quarterlyPrice: 2199,
      yearlyPrice: 7999,
      features: ['Unlimited Data', 'Free Router', '24/7 Support', 'Free Installation', 'Static IP', 'Priority Support'],
      color: 'from-purple-500 to-pink-500',
      popular: false
    },
    {
      id: 'gigabit-1000',
      name: 'Gigabit',
      speed: '1 Gbps',
      icon: <Tv className="h-8 w-8" />,
      monthlyPrice: 1999,
      quarterlyPrice: 5499,
      yearlyPrice: 19999,
      features: ['Unlimited Data', 'Premium Router', '24/7 Priority Support', 'Free Installation', 'Static IP', 'Dedicated Support Manager'],
      color: 'from-red-500 to-orange-500',
      popular: false
    }
  ];

  // Get plan from URL or location state
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const planId = params.get('plan') || location.state?.planId;
    if (planId) {
      const plan = internetPlans.find(p => p.id === planId);
      if (plan) setSelectedPlan(plan);
    }
  }, [location]);

  const getPrice = (plan) => {
    if (billingCycle === 'monthly') return plan.monthlyPrice;
    if (billingCycle === 'quarterly') return plan.quarterlyPrice;
    return plan.yearlyPrice;
  };

  const getSavings = (plan) => {
    const monthlyTotal = plan.monthlyPrice * (billingCycle === 'quarterly' ? 3 : billingCycle === 'yearly' ? 12 : 1);
    const actualPrice = getPrice(plan);
    return monthlyTotal - actualPrice;
  };

  const handleInputChange = (e) => {
    setCustomerDetails({
      ...customerDetails,
      [e.target.name]: e.target.value
    });
  };

  const handlePayment = async () => {
    if (!selectedPlan) {
      setError('Please select a plan');
      return;
    }

    if (!customerDetails.name || !customerDetails.email || !customerDetails.phone) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${API_URL}/api/checkout/create-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          plan_id: selectedPlan.id,
          plan_name: selectedPlan.name,
          speed: selectedPlan.speed,
          amount: getPrice(selectedPlan),
          billing_cycle: billingCycle,
          customer_name: customerDetails.name,
          customer_email: customerDetails.email,
          customer_phone: customerDetails.phone,
          customer_address: customerDetails.address,
          customer_city: customerDetails.city,
          customer_pincode: customerDetails.pincode
        })
      });

      const data = await response.json();

      if (data.success && data.payment_url) {
        // Redirect to ZOHO payment page
        window.location.href = data.payment_url;
      } else {
        setError(data.message || 'Failed to initiate payment. Please try again.');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error('Payment error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-pink-600 py-8">
        <div className="max-w-7xl mx-auto px-6">
          <button 
            onClick={() => navigate('/request-service')}
            className="flex items-center text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ChevronLeft className="h-5 w-5 mr-1" />
            Back to Plans
          </button>
          <h1 className="text-3xl md:text-4xl font-black text-white">
            Secure Checkout
          </h1>
          <p className="text-pink-100 mt-2">Complete your order and get connected today</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Plan Selection & Customer Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Billing Cycle Selection */}
            <Card className="border-0 bg-white/5 backdrop-blur-sm">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Clock className="h-5 w-5 text-red-400" />
                  Select Billing Cycle
                </h2>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { id: 'monthly', label: 'Monthly', discount: null },
                    { id: 'quarterly', label: '3 Months', discount: '10% OFF' },
                    { id: 'yearly', label: 'Annual', discount: '20% OFF' }
                  ].map((cycle) => (
                    <button
                      key={cycle.id}
                      onClick={() => setBillingCycle(cycle.id)}
                      className={`relative p-4 rounded-xl border-2 transition-all ${
                        billingCycle === cycle.id
                          ? 'border-red-500 bg-red-500/20'
                          : 'border-white/20 hover:border-white/40 bg-white/5'
                      }`}
                    >
                      {cycle.discount && (
                        <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                          {cycle.discount}
                        </span>
                      )}
                      <div className={`font-bold ${billingCycle === cycle.id ? 'text-white' : 'text-gray-300'}`}>
                        {cycle.label}
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Plan Selection */}
            <Card className="border-0 bg-white/5 backdrop-blur-sm">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Wifi className="h-5 w-5 text-red-400" />
                  Select Your Plan
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {internetPlans.map((plan) => (
                    <div
                      key={plan.id}
                      onClick={() => setSelectedPlan(plan)}
                      className={`relative cursor-pointer rounded-2xl p-5 border-2 transition-all ${
                        selectedPlan?.id === plan.id
                          ? 'border-red-500 bg-gradient-to-br ' + plan.color + ' bg-opacity-20'
                          : 'border-white/20 hover:border-white/40 bg-white/5'
                      }`}
                    >
                      {plan.popular && (
                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                          MOST POPULAR
                        </span>
                      )}
                      
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`p-2 rounded-xl bg-gradient-to-br ${plan.color} text-white`}>
                          {plan.icon}
                        </div>
                        <div>
                          <h3 className="font-bold text-white">{plan.name}</h3>
                          <p className="text-sm text-gray-400">{plan.speed}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-end gap-1 mb-3">
                        <span className="text-3xl font-black text-white">₹{getPrice(plan)}</span>
                        <span className="text-gray-400 text-sm mb-1">
                          /{billingCycle === 'monthly' ? 'mo' : billingCycle === 'quarterly' ? '3mo' : 'yr'}
                        </span>
                      </div>
                      
                      {getSavings(plan) > 0 && (
                        <div className="text-green-400 text-sm font-semibold mb-3">
                          Save ₹{getSavings(plan)}
                        </div>
                      )}
                      
                      <ul className="space-y-1.5">
                        {plan.features.slice(0, 4).map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm text-gray-300">
                            <CheckCircle2 className="h-4 w-4 text-green-400 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      
                      {selectedPlan?.id === plan.id && (
                        <div className="absolute top-3 right-3">
                          <CheckCircle2 className="h-6 w-6 text-green-400" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Customer Details */}
            <Card className="border-0 bg-white/5 backdrop-blur-sm">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-red-400" />
                  Customer Details
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Full Name *</Label>
                    <Input
                      name="name"
                      value={customerDetails.name}
                      onChange={handleInputChange}
                      placeholder="Enter your full name"
                      className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Email Address *</Label>
                    <Input
                      name="email"
                      type="email"
                      value={customerDetails.email}
                      onChange={handleInputChange}
                      placeholder="Enter your email"
                      className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Phone Number *</Label>
                    <Input
                      name="phone"
                      value={customerDetails.phone}
                      onChange={handleInputChange}
                      placeholder="Enter your phone number"
                      className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Pincode</Label>
                    <Input
                      name="pincode"
                      value={customerDetails.pincode}
                      onChange={handleInputChange}
                      placeholder="Enter pincode"
                      className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-gray-300">Address</Label>
                    <Input
                      name="address"
                      value={customerDetails.address}
                      onChange={handleInputChange}
                      placeholder="Enter your address"
                      className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">City</Label>
                    <Input
                      name="city"
                      value={customerDetails.city}
                      onChange={handleInputChange}
                      placeholder="Enter city"
                      className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Order Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-6">
              <Card className="border-0 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm overflow-hidden">
                <div className="bg-gradient-to-r from-red-600 to-pink-600 p-4">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Order Summary
                  </h2>
                </div>
                <CardContent className="p-6 space-y-6">
                  {selectedPlan ? (
                    <>
                      {/* Selected Plan Details */}
                      <div className="bg-white/10 rounded-xl p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <div className={`p-2 rounded-lg bg-gradient-to-br ${selectedPlan.color} text-white`}>
                            {selectedPlan.icon}
                          </div>
                          <div>
                            <h3 className="font-bold text-white">{selectedPlan.name} Plan</h3>
                            <p className="text-sm text-gray-400">{selectedPlan.speed} Speed</p>
                          </div>
                        </div>
                        <div className="border-t border-white/10 pt-3 mt-3">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-400">Billing Cycle</span>
                            <span className="text-white capitalize">{billingCycle}</span>
                          </div>
                        </div>
                      </div>

                      {/* Price Breakdown */}
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Plan Price</span>
                          <span className="text-white">₹{getPrice(selectedPlan)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Installation</span>
                          <span className="text-green-400">FREE</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Router</span>
                          <span className="text-green-400">FREE</span>
                        </div>
                        {getSavings(selectedPlan) > 0 && (
                          <div className="flex justify-between text-green-400">
                            <span>Savings</span>
                            <span>-₹{getSavings(selectedPlan)}</span>
                          </div>
                        )}
                        <div className="border-t border-white/20 pt-3">
                          <div className="flex justify-between">
                            <span className="text-white font-bold">Total</span>
                            <span className="text-2xl font-black text-white">₹{getPrice(selectedPlan)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Error Message */}
                      {error && (
                        <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 flex items-center gap-2">
                          <AlertCircle className="h-5 w-5 text-red-400" />
                          <span className="text-red-300 text-sm">{error}</span>
                        </div>
                      )}

                      {/* Pay Button */}
                      <Button
                        onClick={handlePayment}
                        disabled={loading}
                        className="w-full h-14 text-lg font-bold bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white shadow-xl shadow-red-500/30"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <Lock className="h-5 w-5 mr-2" />
                            Pay ₹{getPrice(selectedPlan)}
                            <ArrowRight className="h-5 w-5 ml-2" />
                          </>
                        )}
                      </Button>
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <Wifi className="h-12 w-12 text-gray-500 mx-auto mb-3" />
                      <p className="text-gray-400">Select a plan to continue</p>
                    </div>
                  )}

                  {/* Security Badges */}
                  <div className="border-t border-white/10 pt-4">
                    <div className="flex items-center justify-center gap-4 text-xs text-gray-400">
                      <div className="flex items-center gap-1">
                        <Lock className="h-4 w-4" />
                        <span>SSL Secure</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Shield className="h-4 w-4" />
                        <span>Safe Payment</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <BadgeCheck className="h-4 w-4" />
                        <span>Verified</span>
                      </div>
                    </div>
                    <div className="text-center mt-3">
                      <p className="text-xs text-gray-500">Powered by ZOHO Payments</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Trust Indicators */}
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="bg-white/5 rounded-xl p-3 text-center">
                  <div className="text-green-400 font-bold">99.9%</div>
                  <div className="text-xs text-gray-400">Uptime</div>
                </div>
                <div className="bg-white/5 rounded-xl p-3 text-center">
                  <div className="text-blue-400 font-bold">24/7</div>
                  <div className="text-xs text-gray-400">Support</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
